import { c as _c } from "react/compiler-runtime";
import { Workspace as W } from '../workspace';
import { Alert, Skeleton, Tabs, Overlay, Button, Loader, type MantineColor, useMantineTheme, Badge } from '@mantine/core';
import { DateTime as D } from 'luxon';
import { InfoLabel } from 'components/Info';
import { HiGlobe, HiUsers } from 'react-icons/hi';
import { usePortal } from '../portal';
import { useQuerySbSync } from 'utils/db/useQuerySb';
import { WorkspaceTypes as WT } from 'shared/forecast/workspaceTypes';
import { match, P } from 'ts-pattern';
import { fmtDate, fmtNum } from 'shared/format';
import { NotNull } from 'shared/type';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchLicenses, fmtCurrency, getPlans, isLicenseExpired, licenseApi, stripeLicensePlans, type LicensePlan, type Plan, type PlanCurrency, type StripeLicensePlan } from 'shared/forecast/license';
import { env } from 'utils/env';
import { CopyText } from 'components/form/CopyLink';
import { upperFirst } from '@mantine/hooks';
import { licensePlans } from 'shared/forecast/license';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useEffect } from 'react';
import z from 'zod';
import { useQueryString } from 'utils/useQueryString';
import { twMerge } from 'tailwind-merge';
import { openConfirmModal } from '@mantine/modals';
import { nav } from '../nav';
import { IconContext } from 'react-icons';
import { delay, err, ok, okOrThrow } from 'shared/promise';
import { pick, range, sortBy } from 'remeda';
import { useLatest } from 'components/hooks';
import { FaFilePdf } from 'react-icons/fa';
import { doSafe } from 'shared/promise';
import type { GlobalAdminUI } from './subscriptions';
const stripePromise = env.NEXT_PUBLIC_STRIPE_KEY ? loadStripe(env.NEXT_PUBLIC_STRIPE_KEY) : undefined;
type License = NotNull<W.AdminExtra['license']>;
const fetchLicense = async ({
  workspace_id
}: {
  workspace_id: string;
}) => {
  const res = await fetchLicenses({
    workspace_id,
    isSelfHost: env.NEXT_PUBLIC_SELF_HOST
  });
  if (!res.ok) return res;
  const token = res.licenses[0]?.token ?? null;
  return ok(token ? {
    token
  } : {});
};
export const licensePlanMd = {
  starter: {
    color: 'orange'
  },
  team: {
    color: 'purple'
  },
  enterprise: {
    color: 'teal'
  },
  trial: {
    color: 'blue'
  },
  unrestricted: {
    color: 'gray'
  },
  expired: {
    color: 'red'
  }
} satisfies Record<LicensePlan | GlobalAdminUI['status'], {
  color: MantineColor;
}>;
export const planColor = (plan: LicensePlan | GlobalAdminUI['status']): MantineColor => licensePlanMd[plan].color ?? 'gray';
const useAdminSubscription = () => {
  const $ = _c(19);
  const {
    workspace_id
  } = usePortal();
  let t0;
  if ($[0] !== workspace_id) {
    t0 = {
      id: workspace_id
    };
    $[0] = workspace_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const sbSync = useQuerySbSync({
    table: "workspace_global_admin",
    filter: t0,
    schema: WT.zGlobalAdminRow,
    onDbChange: () => {
      licenseTokenQuery.refetch();
    }
  });
  const row = sbSync.data?.[0];
  let t1;
  if ($[2] !== workspace_id) {
    t1 = {
      queryKey: ["license", workspace_id],
      queryFn: () => fetchLicense({
        workspace_id
      }).then(_temp)
    };
    $[2] = workspace_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const licenseTokenQuery = useQuery(t1);
  let t2;
  if ($[4] !== row?.cfg?.license.plan) {
    t2 = doSafe(row?.cfg?.license.plan, _temp2) ?? false;
    $[4] = row?.cfg?.license.plan;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isStripePlan = t2;
  let t3;
  let t4;
  if ($[6] !== workspace_id) {
    t3 = ["invoices", workspace_id];
    t4 = async () => await licenseApi.post("getInvoices", {
      workspace_id
    }).then(okOrThrow);
    $[6] = workspace_id;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isStripePlan || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled: isStripePlan
    };
    $[9] = isStripePlan;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const invoicesQuery = useQuery(t5);
  let t6;
  if ($[13] !== invoicesQuery || $[14] !== licenseTokenQuery || $[15] !== row || $[16] !== sbSync || $[17] !== workspace_id) {
    t6 = {
      workspace_id,
      sbSync,
      licenseTokenQuery,
      invoicesQuery,
      row
    };
    $[13] = invoicesQuery;
    $[14] = licenseTokenQuery;
    $[15] = row;
    $[16] = sbSync;
    $[17] = workspace_id;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
type UseAdminSubscription = ReturnType<typeof useAdminSubscription>;
export const AdminSubscription = t0 => {
  const $ = _c(16);
  const {
    workspace_id,
    sbSync,
    licenseTokenQuery,
    invoicesQuery,
    row
  } = useAdminSubscription();
  let t1;
  if ($[0] !== sbSync.isLoading) {
    t1 = sbSync.isLoading && <Overlay blur={1} opacity={0.5} color="#fff" />;
    $[0] = sbSync.isLoading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== invoicesQuery || $[3] !== licenseTokenQuery || $[4] !== row?.cfg || $[5] !== sbSync || $[6] !== workspace_id) {
    let t3;
    if ($[8] !== invoicesQuery || $[9] !== licenseTokenQuery || $[10] !== sbSync || $[11] !== workspace_id) {
      t3 = cfg => {
        const hostVersion = process.env.NEXT_PUBLIC_HOST_VERSION ?? "latest";
        return <div className="flex flex-col gap-2"><PlanForm adminExtra={cfg} workspace_id={workspace_id} refetch={() => {
            sbSync.refetch();
            invoicesQuery.refetch();
          }} />{cfg.license.selfHost && <div className="pt-4"><Tabs defaultValue="linux" className="mb-4" classNames={{
              tabLabel: "text-md font-semibold text-gray-600"
            }}><Tabs.List><div className="self-center pb-1 pr-6 flex gap-4 items-center"><div className="font-semibold opacity-60">Self-host installation</div></div><Tabs.Tab value="linux">Linux</Tabs.Tab><Tabs.Tab value="windows">Windows</Tabs.Tab><InfoLabel className="ml-auto" helpPath="Self-Hosting-1b88cfc4ab2680feaa0acc5ab94a2807">help</InfoLabel></Tabs.List><Tabs.Panel value="linux" className="pt-3"><CopyText text={`curl -fsSL ${env.URL}/install-sh | bash -s ${hostVersion}`} /></Tabs.Panel><Tabs.Panel value="windows" className="pt-3"><InfoLabel>Windows installation is not available yet</InfoLabel></Tabs.Panel></Tabs><div className="grid grid-cols-[auto,1fr] gap-4 items-center"><div className="text-md text-gray-600">Self-host key</div><CopyText text={licenseTokenQuery.data?.token ?? ""} title="License token copied" message="The license token has been copied to your clipboard" /></div></div>}{stripeLicensePlans.includes(cfg.license.plan as any) && <InvoicesList className="mt-6" workspace_id={workspace_id} invoicesQuery={invoicesQuery} />}</div>;
      };
      $[8] = invoicesQuery;
      $[9] = licenseTokenQuery;
      $[10] = sbSync;
      $[11] = workspace_id;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    t2 = match({
      ...pick(sbSync, ["isSuccess", "isError", "error"]),
      cfg: row?.cfg
    }).with({
      isSuccess: true,
      cfg: P.not(P.nullish).select()
    }, t3).with({
      isError: true,
      error: P.not(P.nullish).select()
    }, _temp3).with({
      isSuccess: true,
      cfg: P.nullish
    }, _temp4).otherwise(_temp5);
    $[2] = invoicesQuery;
    $[3] = licenseTokenQuery;
    $[4] = row?.cfg;
    $[5] = sbSync;
    $[6] = workspace_id;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = <div className="flex flex-col gap-4 p-2"><div className="relative">{t1}{t2}</div></div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
};
const InvoicesList = t0 => {
  const $ = _c(6);
  const {
    className,
    invoicesQuery
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-lg font-semibold text-gray-700 mb-2">Invoices</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== invoicesQuery) {
    t2 = match(invoicesQuery).with({
      isLoading: true
    }, _temp6).with({
      isError: true,
      error: P.not(P.nullish).select()
    }, _temp7).with({
      data: P.not(P.nullish).select()
    }, _temp16).otherwise(_temp17);
    $[1] = invoicesQuery;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className || $[4] !== t2) {
    t3 = <div className={className}>{t1}{t2}</div>;
    $[3] = className;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const PlanForm = t0 => {
  const $ = _c(58);
  const {
    workspace_id,
    adminExtra,
    refetch
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = z.object({
      mode: z.enum(["view", "stripe-return"]).default("view"),
      session_id: z.string().optional(),
      waitForPlan: z.enum(licensePlans).optional()
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    query,
    setQuery
  } = useQueryString(t1);
  const latest = useLatest(adminExtra);
  const {
    license
  } = adminExtra;
  let t2;
  if ($[1] !== latest) {
    t2 = async expectedPlan => {
      const waitStart = D.now();
      while (waitStart.diffNow().as("seconds") < 60) {
        const plan = latest.current.license.plan;
        if (plan != expectedPlan) {
          await delay(1000);
        } else {
          return ok({
            plan
          });
        }
      }
      return err("Timed out waiting for license update");
    };
    $[1] = latest;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const waitForLicenseUpdate = t2;
  let t3;
  if ($[3] !== workspace_id) {
    t3 = {
      mutationFn: async t4 => {
        const {
          plan: plan_0
        } = t4;
        const res = await licenseApi.post("createCheckoutSession", {
          workspace_id,
          plan: plan_0,
          returnParams: {
            mode: "stripe-return",
            waitForPlan: plan_0
          }
        });
        if (!res.ok) {
          throw new Error(res.message);
        }
        return res;
      }
    };
    $[3] = workspace_id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const checkoutMute = useMutation(t3);
  let t4;
  if ($[5] !== workspace_id) {
    t4 = async t5 => {
      const {
        plan: plan_1
      } = t5;
      const res_0 = await licenseApi.post("updateSubscription", {
        workspace_id,
        plan: plan_1
      });
      if (!res_0.ok) {
        throw new Error(res_0.message);
      }
      return res_0;
    };
    $[5] = workspace_id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== refetch) {
    t5 = async d => {
      refetch();
    };
    $[7] = refetch;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = {
      mutationFn: t4,
      onSuccess: t5
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const updateSubscriptionMute = useMutation(t6);
  let t7;
  if ($[12] !== waitForLicenseUpdate || $[13] !== workspace_id) {
    t7 = async t8 => {
      const {
        sessionId,
        expectedPlan: expectedPlan_0
      } = t8;
      const res_1 = await licenseApi.post("sessionStatus", {
        workspace_id,
        sessionId
      });
      if (!res_1.ok) {
        throw new Error(res_1.message);
      }
      if (res_1.sessionStatus == "unpaid") {
        throw new Error("Subscription is unpaid");
      }
      return waitForLicenseUpdate(expectedPlan_0).then(okOrThrow);
    };
    $[12] = waitForLicenseUpdate;
    $[13] = workspace_id;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== setQuery) {
    t8 = async d_0 => {
      setQuery({
        mode: "view"
      });
    };
    $[15] = setQuery;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = {
      mutationFn: t7,
      onSuccess: t8
    };
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const returnMute = useMutation(t9);
  let t10;
  if ($[20] !== query.mode || $[21] !== query.session_id || $[22] !== query.waitForPlan || $[23] !== returnMute) {
    t10 = () => {
      if (query.mode == "stripe-return" && returnMute.isIdle && query.session_id && query.waitForPlan) {
        returnMute.mutate({
          sessionId: query.session_id,
          expectedPlan: query.waitForPlan
        });
      }
    };
    $[20] = query.mode;
    $[21] = query.session_id;
    $[22] = query.waitForPlan;
    $[23] = returnMute;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== query.mode) {
    t11 = [query.mode];
    $[25] = query.mode;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  if ($[27] !== waitForLicenseUpdate || $[28] !== workspace_id) {
    t12 = async t13 => {
      const {
        reactivate
      } = t13;
      const res_2 = await licenseApi.post("cancelSubscription", {
        workspace_id,
        reactivate
      });
      if (!res_2.ok) {
        throw new Error(res_2.message);
      }
      return waitForLicenseUpdate("trial").then(okOrThrow);
    };
    $[27] = waitForLicenseUpdate;
    $[28] = workspace_id;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== refetch) {
    t13 = async d_1 => {
      refetch();
    };
    $[30] = refetch;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t12 || $[33] !== t13) {
    t14 = {
      mutationFn: t12,
      onSuccess: t13
    };
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const cancelSubscriptionMute = useMutation(t14);
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      queryKey: ["plans"],
      queryFn: _temp18,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const plansQuery = useQuery(t15);
  const displayMode = query.mode == "stripe-return" ? "stripe-return" : checkoutMute.isSuccess ? "checkout" : "view";
  let t16;
  if ($[36] !== adminExtra || $[37] !== cancelSubscriptionMute || $[38] !== checkoutMute || $[39] !== displayMode || $[40] !== license || $[41] !== plansQuery || $[42] !== returnMute || $[43] !== setQuery || $[44] !== updateSubscriptionMute) {
    let t17;
    if ($[46] !== adminExtra || $[47] !== cancelSubscriptionMute || $[48] !== checkoutMute || $[49] !== license || $[50] !== plansQuery || $[51] !== updateSubscriptionMute) {
      t17 = () => {
        const {
          plan: plan_2
        } = license;
        return <div className="flex flex-col gap-4 items-start">{cancelSubscriptionMute.isError && <Alert color="red" title="Error cancelling subscription">{cancelSubscriptionMute.error.message}</Alert>}<div className={twMerge("flex flex-wrap gap-4")}>{plan_2 == "trial" && <PlanCard plan="trial" price={<span className="flex items-start leading-none">Free<span className="text-sm font-normal text-gray-500 pl-2"> for 2 weeks</span></span>} adminExtra={adminExtra}>{license.expires && <div className="text-sm text-gray-600">{isLicenseExpired(license) ? "Ended " : "Ends "}<b style={{
                  color: isLicenseExpired(license) ? "var(--mantine-color-red-6)" : undefined
                }}>{fmtDate(license.expires, {
                    showTz: true
                  })}</b></div>}</PlanCard>}{match(plansQuery).with({
              data: P.not(P.nullish).select()
            }, data => data.plans.map(plan_3 => <PlanCard key={plan_3.name} plan={plan_3.name} color={match(plan_3.name).with("starter", _temp19).with("team", _temp20).exhaustive()} price={<Price amount={plan_3.price} currency={plan_3.currency} suffix="/ month" />} adminExtra={adminExtra} onSubscribe={() => {
              if (license == null || license.plan == "trial") {
                checkoutMute.mutate({
                  plan: plan_3.name
                });
              } else {
                if (license.plan != plan_3.name) {
                  updateSubscriptionMute.mutate({
                    plan: plan_3.name
                  });
                }
              }
            }} subscribeLoading={checkoutMute.isPending || updateSubscriptionMute.isPending} onCancel={props => {
              checkoutMute.reset();
              return cancelSubscriptionMute.mutate(props);
            }}><PlanDetails {...plan_3} /></PlanCard>)).with({
              isLoading: true
            }, () => range(0, 2).map(i => <PlanCard key={i} title={<Skeleton h="2em" />} price={<Skeleton h="2.5em" />} adminExtra={adminExtra} customAction={<Skeleton h="2.5em" />} />)).with({
              isError: true,
              error: P.not(P.nullish).select()
            }, _temp21).otherwise(_temp22)}<PlanCard color="teal" plan="enterprise" price="Custom" adminExtra={adminExtra} customAction={<Button className="unstyled" fullWidth={true} variant="filled" component="a" color="teal" href={nav.contactUs.url.url} target="_blank">Contact Us</Button>}><ul className="space-y-2 mb-6"><li className="flex items-center gap-2"><span className="text-green-500 font-bold">✓</span> All Team features</li><li className="flex items-center gap-2"><span className="text-green-500 font-bold">✓</span> Single Sign On</li><li className="flex items-center gap-2"><span className="text-green-500 font-bold">✓</span> Self Hosting</li></ul></PlanCard></div>{checkoutMute.isError && <div><Alert color="red" title="Error creating checkout session">{checkoutMute.error.message}</Alert></div>}</div>;
      };
      $[46] = adminExtra;
      $[47] = cancelSubscriptionMute;
      $[48] = checkoutMute;
      $[49] = license;
      $[50] = plansQuery;
      $[51] = updateSubscriptionMute;
      $[52] = t17;
    } else {
      t17 = $[52];
    }
    let t18;
    if ($[53] !== checkoutMute || $[54] !== setQuery) {
      t18 = () => {
        const {
          data: data_0,
          isError,
          error
        } = checkoutMute;
        return stripePromise && <div className="flex flex-col items-start self-start gap-4"><Button variant="outline" className="self-end" onClick={() => {
            checkoutMute.reset();
            setQuery({
              mode: "view"
            });
          }}>Cancel checkout</Button><EmbeddedCheckoutProvider stripe={stripePromise} options={{
            clientSecret: data_0?.clientSecret
          }}><EmbeddedCheckout className="min-w-[30em]" /></EmbeddedCheckoutProvider>{isError && <Alert color="red" title="Error creating checkout session">{error.message}</Alert>}</div>;
      };
      $[53] = checkoutMute;
      $[54] = setQuery;
      $[55] = t18;
    } else {
      t18 = $[55];
    }
    let t19;
    if ($[56] !== returnMute) {
      t19 = () => {
        const {
          isPending,
          isError: isError_0,
          error: error_0
        } = returnMute;
        return <div>{isPending && <Loader />}{isError_0 && <Alert color="red" title="Error checking session status">{error_0.message}</Alert>}</div>;
      };
      $[56] = returnMute;
      $[57] = t19;
    } else {
      t19 = $[57];
    }
    t16 = match(displayMode).with("view", t17).with("checkout", t18).with("stripe-return", t19).exhaustive();
    $[36] = adminExtra;
    $[37] = cancelSubscriptionMute;
    $[38] = checkoutMute;
    $[39] = displayMode;
    $[40] = license;
    $[41] = plansQuery;
    $[42] = returnMute;
    $[43] = setQuery;
    $[44] = updateSubscriptionMute;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
const PlanCard = t0 => {
  const $ = _c(49);
  const {
    plan,
    title,
    price,
    adminExtra,
    color: t1,
    onSubscribe,
    onCancel,
    showCancel: t2,
    customAction,
    children,
    subscribeLoading
  } = t0;
  const color = t1 === undefined ? "blue" : t1;
  const showCancel = t2 === undefined ? true : t2;
  const theme = useMantineTheme();
  const mainColor = theme.colors[color]?.[6];
  let t3;
  if ($[0] !== plan) {
    t3 = stripeLicensePlans.includes(plan as any) && stripePromise;
    $[0] = plan;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const showSubscribe = t3;
  const {
    license,
    stripe
  } = adminExtra;
  const isCurrent = license.plan != null && license.plan == plan;
  let t4;
  if ($[2] !== color || $[3] !== customAction || $[4] !== isCurrent || $[5] !== license.plan || $[6] !== onCancel || $[7] !== onSubscribe || $[8] !== plan || $[9] !== showCancel || $[10] !== showSubscribe || $[11] !== stripe || $[12] !== subscribeLoading) {
    let t5;
    if ($[14] !== color || $[15] !== isCurrent || $[16] !== license.plan || $[17] !== onCancel || $[18] !== onSubscribe || $[19] !== plan || $[20] !== showCancel || $[21] !== showSubscribe || $[22] !== stripe || $[23] !== subscribeLoading) {
      t5 = () => {
        const canUpdate = showSubscribe && isCurrent && showCancel && onCancel && stripe;
        return <div className="flex flex-col gap-2">{canUpdate && !stripe.cancel_at && <Button fullWidth={true} variant="outline" color="red" onClick={() => {
            openConfirmModal({
              title: "Cancel Subscription",
              centered: true,
              children: <div className="pb-4">Are you sure you want to cancel your subscription?<ul className="mt-2 space-y-2"><li>You will be downgraded to the trial plan at the end of the current billing period (<span className="font-semibold">{fmtDate(D.fromSeconds(stripe.current_period_end), {
                        showTz: true
                      })}</span>)</li><li>Once downgraded you will have read-only access to your questions and forecasts</li></ul></div>,
              labels: {
                confirm: "Cancel Subscription",
                cancel: "Keep Subscription"
              },
              confirmProps: {
                color: "red",
                variant: "filled"
              },
              onConfirm: () => onCancel({}),
              size: "lg"
            });
          }}>Cancel Subscription</Button>}{canUpdate && stripe.cancel_at && <><div className="opacity-70">Scheduled for cancellation on{" "}<span className="font-semibold opacity-90">{fmtDate(D.fromSeconds(stripe.cancel_at), {
                  showTz: true
                })}</span></div><Button size="md" fullWidth={true} variant="filled" color={color} onClick={() => onCancel({
              reactivate: true
            })}>Reactivate Subscription</Button></>}{!isCurrent && showSubscribe && onSubscribe && <Button size="md" fullWidth={true} variant="filled" color={color} onClick={() => {
            if (license.plan != "trial") {
              openConfirmModal({
                title: "Change Subscription Plan",
                centered: true,
                children: <div className="pb-4">Are you sure you want to change your subscription plan?<ul className="mt-2 space-y-2"><li>The plan change will take effect immediately</li><li>You'll be charged a prorated amount, reflecting the difference in price between your old and new plan, adjusted for the remaining time in your billing cycle.</li><li>Additional users and participants for this billing period will be charged at the new plan price.</li></ul></div>,
                labels: {
                  confirm: "Change Plan",
                  cancel: "Keep Current Plan"
                },
                confirmProps: {
                  color,
                  variant: "filled"
                },
                onConfirm: onSubscribe,
                size: "lg"
              });
            } else {
              onSubscribe();
            }
          }} loading={subscribeLoading} disabled={stripe?.cancel_at != null}>{license.plan == "trial" ? `Subscribe to ${upperFirst(plan ?? "")}` : "Change to this plan"}</Button>}</div>;
      };
      $[14] = color;
      $[15] = isCurrent;
      $[16] = license.plan;
      $[17] = onCancel;
      $[18] = onSubscribe;
      $[19] = plan;
      $[20] = showCancel;
      $[21] = showSubscribe;
      $[22] = stripe;
      $[23] = subscribeLoading;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    t4 = match(customAction).with(P.not(P.nullish), _temp23).otherwise(t5);
    $[2] = color;
    $[3] = customAction;
    $[4] = isCurrent;
    $[5] = license.plan;
    $[6] = onCancel;
    $[7] = onSubscribe;
    $[8] = plan;
    $[9] = showCancel;
    $[10] = showSubscribe;
    $[11] = stripe;
    $[12] = subscribeLoading;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const action = t4;
  let t5;
  if ($[25] !== isCurrent || $[26] !== mainColor) {
    t5 = isCurrent ? {
      borderColor: mainColor,
      borderWidth: "3px"
    } : {};
    $[25] = isCurrent;
    $[26] = mainColor;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== mainColor || $[29] !== t5) {
    t6 = {
      "--mantine-color-primary-filled": mainColor,
      "--mantine-color-primary-text": mainColor,
      ...t5
    };
    $[28] = mainColor;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const t7 = t6 as any;
  let t8;
  if ($[31] !== isCurrent || $[32] !== plan || $[33] !== title) {
    t8 = plan && <div className="relative w-full h-[180px] rounded-t-lg overflow-hidden"><img src={`/subscription/${plan}.png`} alt={`${plan} plan banner`} className="w-full h-full object-cover" onError={_temp24} />{isCurrent && <div className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold text-white rounded-full bg-[--mantine-color-primary-filled] border-2 border-white">Current Plan</div>}<div className="absolute bottom-0 left-0 right-0 p-4"><h3 className="text-[1.8em] font-extrabold opacity-90 [color:_var(--mantine-color-primary-filled)] text-stroke-white-2 tracking-wider">{title ?? upperFirst(plan)}</h3></div></div>;
    $[31] = isCurrent;
    $[32] = plan;
    $[33] = title;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t8}</div>;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== price) {
    t10 = <div className="text-3xl font-bold mb-3">{price}</div>;
    $[37] = price;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== action) {
    t11 = action && <div className="mt-auto">{action}</div>;
    $[39] = action;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== children || $[42] !== t10 || $[43] !== t11) {
    t12 = <div className="flex flex-col gap-2 p-4 grow">{t10}{children}{t11}</div>;
    $[41] = children;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t12 || $[46] !== t7 || $[47] !== t9) {
    t13 = <div style={t7} className="rounded-lg border flex flex-col gap-2 min-w-[25em] max-w-[30em] flex-1">{t9}{t12}</div>;
    $[45] = t12;
    $[46] = t7;
    $[47] = t9;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  return t13;
};
const Price = t0 => {
  const $ = _c(17);
  const {
    amount,
    currency,
    suffix,
    className,
    hideCurrency
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = twMerge("flex items-start leading-none", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== amount || $[3] !== currency) {
    t2 = fmtCurrency(amount, {
      currency
    });
    $[2] = amount;
    $[3] = currency;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== currency || $[6] !== hideCurrency) {
    t3 = !hideCurrency && <div>{currency}</div>;
    $[5] = currency;
    $[6] = hideCurrency;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== suffix) {
    t4 = suffix && <div>{suffix}</div>;
    $[8] = suffix;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="text-sm font-normal text-gray-500 ml-2 flex items-center gap-1">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t5) {
    t6 = <span className={t1}>{t2}{t5}</span>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
const PlanDetails = t0 => {
  const $ = _c(37);
  const {
    includedUsers,
    includedParticipants,
    priceUsers,
    priceParticipants,
    currency,
    currencySymbol
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      color: "var(--mantine-color-primary-filled)",
      size: "1.4em"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HiUsers />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== includedUsers) {
    t3 = <div className="flex items-center gap-2">{t2} <b>{includedUsers} users</b></div>;
    $[2] = includedUsers;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== currency || $[5] !== currencySymbol || $[6] !== priceUsers) {
    t4 = fmtCurrency(priceUsers, {
      currency,
      currencySymbol
    });
    $[4] = currency;
    $[5] = currencySymbol;
    $[6] = priceUsers;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <b>{t4}</b>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span> / month</span>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = <div className="opacity-70">Additional users: {t5}{t6}</div>;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t7) {
    t8 = <div className="space-y-1">{t3}{t7}</div>;
    $[13] = t3;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <HiGlobe />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== includedParticipants) {
    t10 = <b>{includedParticipants} participants</b>;
    $[17] = includedParticipants;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="opacity-60"> / month</span>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10) {
    t12 = <div className="flex items-center gap-2">{t9} {t10} {t11}</div>;
    $[20] = t10;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== currency || $[23] !== currencySymbol || $[24] !== priceParticipants) {
    t13 = fmtCurrency(priceParticipants, {
      currency,
      currencySymbol
    });
    $[22] = currency;
    $[23] = currencySymbol;
    $[24] = priceParticipants;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <b>{t13}</b>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <InfoLabel>A participant is someone invited to forecast on a question. Only new participants are counted in each month.</InfoLabel>;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14) {
    t16 = <div className="opacity-70 space-y-2">Additional participants: {t14}{t15}</div>;
    $[29] = t14;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t12 || $[32] !== t16) {
    t17 = <div className="space-y-1">{t12}{t16}</div>;
    $[31] = t12;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17 || $[35] !== t8) {
    t18 = <IconContext.Provider value={t1}><div className="flex flex-col gap-4 pb-6 text-gray-800">{t8}{t17}</div></IconContext.Provider>;
    $[34] = t17;
    $[35] = t8;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};
function _temp(l) {
  if (!l.ok) {
    throw new Error(l.message);
  }
  return l;
}
function _temp2(plan) {
  return stripeLicensePlans.includes(plan as any);
}
function _temp3(e) {
  return <Alert color="red" title="Error fetching license">{e.message}</Alert>;
}
function _temp4() {
  return <div className="text-gray-600">This is an unrestricted workspace.</div>;
}
function _temp5() {
  return <></>;
}
function _temp6() {
  return <Skeleton h="10em" w="34em" />;
}
function _temp7(e) {
  return <Alert color="red" title="Error loading invoices">{e.message}</Alert>;
}
function _temp8(inv) {
  return inv.created;
}
function _temp9() {
  return "green";
}
function _temp10() {
  return "blue";
}
function _temp11() {
  return "red";
}
function _temp12() {
  return "gray";
}
function _temp13() {
  return "upcoming";
}
function _temp14(s) {
  return s;
}
function _temp15(inv_0) {
  return <div key={inv_0.id} className="flex flex-col border rounded-md"><div className="p-4 flex items-start justify-between "><div className="flex flex-col"><div className="font-medium">{inv_0.number ? `Invoice #${inv_0.number}` : "Next invoice estimate"}</div><div className="text-sm text-gray-500">{fmtDate(D.fromSeconds(inv_0.created), {
            showTz: true
          })}</div></div><div className="flex items-center gap-4">{inv_0.invoice_pdf && <a href={inv_0.invoice_pdf} className="flex items-center gap-1"><FaFilePdf /> view</a>}<Badge color={match(inv_0.status).with("paid", _temp9).with("open", _temp10).with("uncollectible", _temp11).otherwise(_temp12)}>{match(inv_0.status).with("draft", _temp13).otherwise(_temp14)}</Badge><Price className="text-lg font-extrabold" amount={inv_0.amount} currency={inv_0.currency} /></div></div>{inv_0.lines && inv_0.lines.length > 0 && <div className="px-4 pb-4">{inv_0.lines.map(line => <div key={`${line.description}-${line.amount}`} className="flex justify-between text-sm"><div className="opacity-60">{line.description}</div><Price className="font-semibold" amount={line.amount} currency={inv_0.currency} hideCurrency={true} /></div>)}</div>}</div>;
}
function _temp16(data) {
  return <div className="flex flex-col gap-2items-start"><div className="flex flex-col gap-2 w-[34em]">{sortBy(data.invoices, [_temp8, "desc"]).map(_temp15)}</div></div>;
}
function _temp17() {
  return <></>;
}
function _temp18() {
  return getPlans().then(okOrThrow);
}
function _temp19() {
  return "orange";
}
function _temp20() {
  return "purple";
}
function _temp21(e) {
  return <Alert color="red" title="Error loading plans">{e.message}</Alert>;
}
function _temp22() {
  return <></>;
}
function _temp23(c) {
  return c;
}
function _temp24(e) {
  e.currentTarget.style.display = "none";
}